import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Feature {
  title: string;
  description: string;
  icon: JSX.Element;
}

const features: Feature[] = [
  {
    title: 'Embed in minutes',
    description: 'One script tag drops a branded assistant into any web app — no rebuild, no migration.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    title: 'Grounded, not guessing',
    description: 'Answers come from your live APIs and documents, with citations. It refuses to invent data.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: 'Permission-aware',
    description: "Respects each user's role and scope, so people only ever see what they're allowed to.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
  },
  {
    title: 'White-label & multi-tenant',
    description: 'Your logo, colors, name and domain — with isolated configuration and data per customer.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Nucleus: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="nucleus" className="section-padding bg-dark-blue relative overflow-hidden">
      {/* Background decorative elements — consistent with Solutions */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute right-0 top-1/4 w-72 h-72 bg-electric-blue/10 rounded-full blur-3xl"></div>
      <div className="absolute left-0 bottom-1/4 w-80 h-80 bg-teal-accent/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left: pitch */}
          <div>
            <motion.span
              variants={itemVariants}
              className="inline-block px-3 py-1 mb-5 text-sm font-semibold rounded-full border border-electric-blue/40 text-electric-blue"
            >
              Flagship Product
            </motion.span>

            <motion.h2 variants={itemVariants} className="text-3xl md:text-5xl font-bold mb-5">
              <span className="gradient-text">Nucleus</span> — the AI agent you embed in your product
            </motion.h2>

            <motion.p variants={itemVariants} className="text-lg text-gray-300 mb-8 max-w-xl">
              A multi-tenant, embeddable AI assistant that answers your users' questions from your own data and
              APIs — grounded, permission-aware, and fully branded as yours.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                className="px-6 py-3 bg-gradient-to-r from-electric-blue to-teal-accent rounded-md text-deep-blue font-semibold hover:shadow-neon-lg transition-all"
              >
                Talk to us
              </a>
              <span className="text-sm text-gray-400">
                Live in production, powering the Varri DMRV platform's field teams.
              </span>
            </motion.div>
          </div>

          {/* Right: assistant panel mockup — echoes the real embeddable panel */}
          <motion.div variants={itemVariants} className="relative">
            <div className="mx-auto w-full max-w-sm rounded-2xl bg-deep-blue border border-electric-blue/30 shadow-neon overflow-hidden animate-float">
              {/* Panel header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-dark-blue border-b border-electric-blue/20">
                <span className="w-2.5 h-2.5 rounded-full bg-electric-blue"></span>
                <span className="text-sm font-semibold text-white">Ask Varri</span>
                <span className="ml-auto text-xs text-gray-500">Nucleus</span>
              </div>
              {/* Panel body */}
              <div className="p-4 space-y-3">
                <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-gradient-to-r from-electric-blue to-teal-accent px-3.5 py-2 text-sm text-deep-blue font-medium">
                  How many farmers are pending validator review?
                </div>
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-dark-blue border border-electric-blue/20 px-3.5 py-2 text-sm text-gray-200">
                  <span className="font-semibold text-white">42 farmers</span> are pending review — 18 at pipe
                  installation and 24 at drying. Want the list?
                </div>
              </div>
              {/* Composer */}
              <div className="px-4 pb-4">
                <div className="flex items-center gap-2 rounded-2xl border border-electric-blue/20 bg-dark-blue px-3.5 py-2.5">
                  <span className="text-sm text-gray-500 flex-1">Ask a question…</span>
                  <span className="w-8 h-8 rounded-xl bg-gradient-to-r from-electric-blue to-teal-accent flex items-center justify-center text-deep-blue">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Feature grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16"
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className="bg-deep-blue/80 p-6 rounded-lg border border-electric-blue/20 hover:shadow-neon hover:scale-105 transition-all"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-electric-blue to-teal-accent rounded-full flex items-center justify-center mb-4 text-deep-blue">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Nucleus;
